// Cart Entries Observer with Total Updates - FIXED VERSION WITH ROUNDING
class CartEntriesObserver {
  constructor() {
    this.entriesCountElement = document.querySelector('.entries-banner-count');
    this.totalValueElement = document.querySelector('.totals__total-value');
    this.cartForm = document.getElementById('cart');
    
    if (!this.entriesCountElement || !this.totalValueElement || !this.cartForm) {
      console.warn('Required cart elements not found');
      return;
    }
    
    this.entriesMultiplier = parseFloat(this.entriesCountElement.dataset.multiplier) || 1;
    this.maxEntriesLimit = parseInt(this.entriesCountElement.dataset.maxLimit) || 0;
    this.isUpdating = false;
    
    this.init();
  }
  
  init() {
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    // Listen for quantity input changes
    const quantityInputs = this.cartForm.querySelectorAll('input[name="updates[]"]');
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.handleQuantityChange(e.target);
      });
    });
    
    // Listen for quantity buttons
    const quantityButtons = this.cartForm.querySelectorAll('.quantity__button');
    quantityButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        setTimeout(() => {
          const input = button.closest('.quantity').querySelector('.quantity__input');
          this.handleQuantityChange(input);
        }, 100);
      });
    });
    
    // Listen for remove buttons
    const removeButtons = this.cartForm.querySelectorAll('cart-remove-button');
    removeButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => this.updateCartTotals(), 500);
      });
    });
    
  }
  
  handleQuantityChange(input) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    const itemId = input.dataset.index;
    const newQuantity = parseInt(input.value);
    
    // Show loading state
    this.showLoading();
    
    // Update via Shopify's cart API
    this.updateCartItem(itemId, newQuantity);
  }
  
  updateCartItem(itemId, quantity) {
    const formData = new FormData();
    formData.append('quantity', quantity);
    formData.append('id', itemId);
    
    fetch('/cart/change.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(cart => {
      // Validate cart data
      if (!cart || typeof cart.total_price === 'undefined') {
        throw new Error('Invalid cart data received');
      }
      
      // Update both totals and entries
      this.updateTotalDisplay(cart.total_price);
      this.updateEntriesDisplay(cart.total_price);
      
      // Also update individual item totals if they exist
      this.updateItemTotals(cart);
      
      this.isUpdating = false;
      this.hideLoading();
      
      // Trigger custom event for cart notification and other components
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart },
        bubbles: true 
      }));
      
      // Also trigger the legacy cart update event
      if (window.pubsub) {
        window.pubsub.publish('cart:updated', cart);
      }
    })
    .catch(error => {
      console.error('Error updating cart:', error);
      this.isUpdating = false;
      this.hideLoading();
      
      // Show user-friendly error
      this.showError('Unable to update cart. Please try again.');
      
      // Fallback to full refresh
      setTimeout(() => this.updateCartTotals(), 500);
    });
  }
  
  updateCartTotals() {
    if (this.isUpdating) return;
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updateTotalDisplay(cart.total_price);
        this.updateEntriesDisplay(cart.total_price);
        this.updateItemTotals(cart);
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
        this.updateFromDOM(); // Fallback method
      });
  }
  
  updateTotalDisplay(totalPrice) {
    // Validate input
    if (isNaN(totalPrice) || totalPrice === null || totalPrice === undefined) {
      console.warn('Invalid total price:', totalPrice);
      totalPrice = 0;
    }
    
    // Format the total price as currency
    const formattedTotal = this.formatCurrency(totalPrice);
    
    // Update the main total display
    if (this.totalValueElement) {
      this.totalValueElement.textContent = formattedTotal;
    }
    
    // Also update any other total elements that might exist
    const allTotalElements = document.querySelectorAll('.totals__total-value');
    allTotalElements.forEach(element => {
      if (element !== this.totalValueElement) {
        element.textContent = formattedTotal;
      }
    });
  }
  
  updateEntriesDisplay(totalPrice) {
    // Validate input
    if (isNaN(totalPrice) || totalPrice === null || totalPrice === undefined) {
      console.warn('Invalid total price for entries:', totalPrice);
      totalPrice = 0;
    }
    
    // Convert cents to dollars and round UP to nearest whole dollar
    const totalValueInDollars = totalPrice / 100;
    const roundedTotalValue = Math.ceil(totalValueInDollars);
    
    // Calculate entries using the rounded total
    let entries = Math.round(roundedTotalValue * this.entriesMultiplier);
    
    if (this.maxEntriesLimit > 0 && entries > this.maxEntriesLimit) {
      entries = this.maxEntriesLimit;
    }
    
    this.animateCount(entries);
  }
  
  updateItemTotals(cart) {
    // Update individual line item totals if they exist
    if (!cart || !cart.items) return;
    
    cart.items.forEach(item => {
      const itemTotalElement = document.querySelector(`[data-variant-id="${item.variant_id}"] .cart-item__totals .price`);
      if (itemTotalElement && item.final_line_price !== undefined) {
        const formatted = this.formatCurrency(item.final_line_price);
        if (itemTotalElement.textContent !== formatted) {
          itemTotalElement.textContent = formatted;
        }
      }
    });
  }
  
  updateFromDOM() {
    // Fallback method: calculate from DOM elements
    let calculatedTotal = 0;
    const itemElements = document.querySelectorAll('.cart-item');
    
    itemElements.forEach(item => {
      const quantityInput = item.querySelector('input[name="updates[]"]');
      const priceElement = item.querySelector('.cart-item__final-price, .product-option');
      
      if (quantityInput && priceElement) {
        const quantity = parseInt(quantityInput.value);
        const priceText = priceElement.textContent;
        const price = this.parseCurrencyToFloat(priceText);
        
        if (!isNaN(quantity) && !isNaN(price)) {
          calculatedTotal += quantity * price;
        }
      }
    });
    
    // Convert to cents for consistency
    const totalInCents = Math.round(calculatedTotal * 100);
    this.updateTotalDisplay(totalInCents);
    this.updateEntriesDisplay(totalInCents);
  }
  
  formatCurrency(cents) {
    // Validate input
    if (isNaN(cents) || cents === null || cents === undefined) {
      console.warn('Invalid price value:', cents);
      return '$0.00';
    }
    
    // Convert cents to formatted currency string
    const amount = cents / 100;
    
    // Use Shopify's format if available, otherwise use basic formatting
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    
    // Basic formatting as fallback
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }
  
  parseCurrencyToFloat(currencyString) {
    if (!currencyString) return 0;
    
    const numericString = currencyString.replace(/[^0-9.,]/g, '');
    let cleaned = numericString;
    
    if (numericString.includes(',') && numericString.includes('.')) {
      if (numericString.lastIndexOf(',') > numericString.lastIndexOf('.')) {
        cleaned = numericString.replace(/\./g, '').replace(',', '.');
      } else {
        cleaned = numericString.replace(/,/g, '');
      }
    } else if (numericString.includes(',')) {
      const commaCount = (numericString.match(/,/g) || []).length;
      if (commaCount === 1 && numericString.indexOf(',') > numericString.length - 4) {
        cleaned = numericString.replace(',', '.');
      } else {
        cleaned = numericString.replace(/,/g, '');
      }
    }
    
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  animateCount(targetCount) {
    const currentCount = parseInt(this.entriesCountElement.textContent) || 0;
    
    if (currentCount === targetCount) return;
    
    this.entriesCountElement.classList.add('updating');
    
    const duration = 300;
    const steps = 10;
    const stepValue = (targetCount - currentCount) / steps;
    const stepDuration = duration / steps;
    
    let currentStep = 0;
    
    const animate = () => {
      currentStep++;
      const newValue = Math.round(currentCount + (stepValue * currentStep));
      
      if (currentStep < steps) {
        this.entriesCountElement.textContent = newValue;
        setTimeout(animate, stepDuration);
      } else {
        this.entriesCountElement.textContent = targetCount;
        this.entriesCountElement.classList.remove('updating');
      }
    };
    
    animate();
  }
  
  showLoading() {
    // Add loading class to relevant elements
    document.querySelectorAll('.totals__total-value, .entries-banner-count').forEach(el => {
      el.classList.add('loading');
    });
  }
  
  hideLoading() {
    // Remove loading class
    document.querySelectorAll('.totals__total-value, .entries-banner-count').forEach(el => {
      el.classList.remove('loading');
    });
  }
  
  showError(message) {
    // Show error to user (you can customize this)
    const errorContainer = document.getElementById('cart-errors');
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.style.display = 'block';
      
      // Hide error after 3 seconds
      setTimeout(() => {
        errorContainer.textContent = '';
        errorContainer.style.display = 'none';
      }, 3000);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartEntriesObserver();
  });
} else {
  new CartEntriesObserver();
}